import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import * as Sentry from "@sentry/react";
import { useAuth } from "@workos-inc/authkit-react";

import { ApiError, OfflineError, apiFetchMultipart } from "@/lib/api";

export type VoiceState = "idle" | "recording" | "processing" | "error";

export type VoiceError =
  | "permission_denied"
  | "unsupported"
  | "recording_failed"
  | "request_failed"
  | "no_audio";

export interface VoiceParseResult {
  /** Map of metadata field id → parsed value. Remarks field (when present)
   * carries the server's timestamp-wrapped cleaned-up block ready for the
   * client-side append rule. */
  fields: Record<string, unknown>;
  /** Always null in the Django port (plan is excluded from voice-fill). */
  planName: string | null;
  /** Tag NAMES the model matched. Client merges them additively by
   * case-insensitive name match. */
  tagNames: string[];
  /** 0..1, model self-reported. Informational. */
  confidence: number;
  /** Short English note when the model couldn't interpret the speaker's
   * intent; null otherwise. */
  unmatchedIntent: string | null;
}

interface UseVoiceCaptureOptions {
  projectId: string;
  mode: "create" | "edit";
  /** Required when `mode === "edit"`. The server fetches the record from
   * the DB so the prompt can include existing values. */
  recordId?: string | null;
}

interface UseVoiceCaptureReturn {
  state: VoiceState;
  result: VoiceParseResult | null;
  error: VoiceError | null;
  /** Milliseconds elapsed since recording started. Updated every 500ms while
   * state === "recording"; resets to 0 otherwise. Throttled to avoid
   * re-render storms on the host component. */
  elapsedMs: number;
  start: () => Promise<void>;
  stop: () => void;
  reset: () => void;
  isSupported: boolean;
}

// Hard cap on a single recording. Balances the user's freedom to describe
// a record against the server's body-size limit and runaway-recording risk
// if the user walks away after tapping the mic. 60s of Opus at ~32 kbps is
// ~240 KB — plenty of headroom.
export const MAX_RECORDING_MS = 60_000;

// Gemini accepts webm/mp4/ogg audio directly; we just need a filename
// whose extension matches the container so the server's media-type sniff is
// unambiguous. MediaRecorder's mimeType may include codec params
// ("audio/webm;codecs=opus") — strip those before splitting.
function filenameForMime(mime: string): string {
  const base = mime.split(";")[0].trim();
  const ext = base.split("/")[1] || "webm";
  return `voice.${ext}`;
}

export function useVoiceCapture(
  opts: UseVoiceCaptureOptions,
): UseVoiceCaptureReturn {
  const [state, setState] = useState<VoiceState>("idle");
  const [result, setResult] = useState<VoiceParseResult | null>(null);
  const [error, setError] = useState<VoiceError | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);

  const { getAccessToken } = useAuth();

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const abortedRef = useRef(false);
  const maxDurationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recordingStartRef = useRef<number>(0);

  const clearMaxDurationTimer = useCallback(() => {
    if (maxDurationTimerRef.current !== null) {
      clearTimeout(maxDurationTimerRef.current);
      maxDurationTimerRef.current = null;
    }
  }, []);

  const clearElapsedTimer = useCallback(() => {
    if (elapsedTimerRef.current !== null) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
  }, []);

  // Latest-value ref so the async onstop handler reads the up-to-date
  // mode/recordId/projectId without re-creating start() on every parent
  // render.
  const optsRef = useRef(opts);
  useEffect(() => {
    optsRef.current = opts;
  }, [opts]);

  const isSupported = useSyncExternalStore(
    () => () => {},
    () =>
      !!navigator.mediaDevices?.getUserMedia &&
      typeof MediaRecorder !== "undefined",
    () => false,
  );

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    mediaRecorderRef.current = null;
    chunksRef.current = [];
  }, []);

  useEffect(() => {
    return () => {
      clearMaxDurationTimer();
      clearElapsedTimer();
      stopStream();
    };
  }, [stopStream, clearMaxDurationTimer, clearElapsedTimer]);

  const reset = useCallback(() => {
    abortedRef.current = true;
    clearMaxDurationTimer();
    clearElapsedTimer();
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state === "recording") {
      try {
        recorder.stop();
      } catch {
        // ignore — recorder may already be inactive
      }
    }
    stopStream();
    setResult(null);
    setError(null);
    setElapsedMs(0);
    setState("idle");
  }, [stopStream, clearMaxDurationTimer, clearElapsedTimer]);

  const start = useCallback(async () => {
    if (!isSupported) {
      setError("unsupported");
      setState("error");
      return;
    }
    if (
      mediaRecorderRef.current?.state === "recording" ||
      state === "processing"
    ) {
      return;
    }

    setError(null);
    setResult(null);
    abortedRef.current = false;

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      console.warn("Mic permission denied", err);
      setError("permission_denied");
      setState("error");
      return;
    }
    streamRef.current = stream;
    chunksRef.current = [];

    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream);
    } catch (err) {
      console.warn("MediaRecorder init failed", err);
      stopStream();
      setError("unsupported");
      setState("error");
      return;
    }

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onerror = () => {
      clearMaxDurationTimer();
      clearElapsedTimer();
      stopStream();
      setError("recording_failed");
      setState("error");
    };

    recorder.onstop = async () => {
      clearMaxDurationTimer();
      clearElapsedTimer();
      const mimeType =
        recorder.mimeType || chunksRef.current[0]?.type || "audio/webm";
      const audio = new Blob(chunksRef.current, { type: mimeType });
      stopStream();

      if (abortedRef.current) return;

      if (audio.size === 0) {
        setError("no_audio");
        setState("error");
        return;
      }

      setState("processing");

      try {
        const { projectId, mode, recordId } = optsRef.current;
        const form = new FormData();
        form.append("audio", audio, filenameForMime(mimeType));
        form.append("mode", mode);
        if (mode === "edit" && recordId) {
          form.append("record_id", recordId);
        }

        const parsed = await apiFetchMultipart<VoiceParseResult>(
          `/projects/1.0/projects/${projectId}/voice-log/parse/`,
          form,
          getAccessToken,
        );

        if (abortedRef.current) return;
        setResult(parsed);
        setState("idle");
      } catch (err) {
        console.error("Voice processing error", err);
        // The hook collapses every failure into the same `request_failed`
        // user-facing copy, so without an explicit capture we lose all
        // diagnostic signal — backend Sentry only sees requests that
        // actually reach the server. Tag the failure class + audio shape
        // so we can tell network/CORS/TypeError apart from a real backend
        // 4xx/5xx the next time someone reports it.
        const { projectId, mode, recordId } = optsRef.current;
        const failureClass =
          err instanceof ApiError
            ? "api_error"
            : err instanceof OfflineError
              ? "offline"
              : "unknown";
        Sentry.captureException(err, {
          level: err instanceof OfflineError ? "warning" : "error",
          tags: {
            area: "voice-log",
            "voice.failure_class": failureClass,
            "voice.status":
              err instanceof ApiError ? String(err.status) : "n/a",
            "voice.mode": mode,
            "voice.has_record_id": String(Boolean(recordId)),
          },
          contexts: {
            voice: {
              projectId,
              mode,
              recordId: recordId ?? null,
              audioBytes: audio.size,
              mimeType,
              apiErrorBody: err instanceof ApiError ? err.body : undefined,
            },
          },
        });
        if (abortedRef.current) return;
        setError("request_failed");
        setState("error");
      }
    };

    mediaRecorderRef.current = recorder;
    recorder.start();
    recordingStartRef.current = Date.now();
    setElapsedMs(0);
    setState("recording");

    elapsedTimerRef.current = setInterval(() => {
      setElapsedMs(Date.now() - recordingStartRef.current);
    }, 500);

    maxDurationTimerRef.current = setTimeout(() => {
      maxDurationTimerRef.current = null;
      const active = mediaRecorderRef.current;
      if (active && active.state === "recording") {
        try {
          active.stop();
        } catch {
          // ignore
        }
      }
    }, MAX_RECORDING_MS);
  }, [
    isSupported,
    state,
    stopStream,
    clearMaxDurationTimer,
    clearElapsedTimer,
    getAccessToken,
  ]);

  const stop = useCallback(() => {
    clearMaxDurationTimer();
    clearElapsedTimer();
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== "recording") return;
    recorder.stop();
  }, [clearMaxDurationTimer, clearElapsedTimer]);

  return {
    state,
    result,
    error,
    elapsedMs,
    start,
    stop,
    reset,
    isSupported,
  };
}
